import { Link } from 'react-router';

import { useDefinitions, useInstances, useMeta, useStatus } from '@/api';
import { Badge, Button, Card, PageHeader, ProtocolBadge, StatusDot } from '@/components/ui';
import { timeAgo } from '@/lib/format';

function Stat({ label, value, tone }: { label: string; value: number | string; tone?: string }) {
	return (
		<div className="rounded-lg border border-border bg-surface px-4 py-3">
			<div className="text-xs text-fg-muted">{label}</div>
			<div className={`mt-1 text-2xl font-semibold tabular-nums ${tone ?? ''}`}>{value}</div>
		</div>
	);
}

export function Dashboard() {
	const status = useStatus();
	const meta = useMeta();
	const instances = useInstances();
	const defs = useDefinitions();
	const s = status.data;
	const pct = s ? Math.min(100, (s.samplesPerSec / s.maxSamplesPerSec) * 100) : 0;
	const problems = (instances.data ?? []).filter((i) => i.status === 'crashed' || i.status === 'invalid');
	return (
		<>
			<PageHeader
				title="Overview"
				subtitle="A fleet of simulated sensors, served over their native protocols."
				actions={
					<Link to="/instances/new">
						<Button variant="primary">New sensor</Button>
					</Link>
				}
			/>
			<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
				<Stat label="Sensors" value={s?.instances.total ?? '–'} />
				<Stat label="Running" value={s?.instances.running ?? '–'} tone="text-ok" />
				<Stat label="Stopped" value={s?.instances.stopped ?? '–'} />
				<Stat label="Need attention" value={s ? s.instances.crashed + s.instances.invalid : '–'} tone={problems.length ? 'text-danger' : ''} />
			</div>

			<div className="mt-4 grid gap-4 lg:grid-cols-3">
				<Card title="Sample budget" className="lg:col-span-1">
					<div className="flex items-baseline justify-between text-sm">
						<span className="font-mono tabular-nums">{s ? s.samplesPerSec.toFixed(1) : '–'} samples/s</span>
						<span className="text-xs text-fg-muted">of {s?.maxSamplesPerSec ?? '–'}</span>
					</div>
					<div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
						<div className={`h-full rounded-full ${pct > 85 ? 'bg-warn' : 'bg-accent'}`} style={{ width: `${pct}%` }} />
					</div>
					<p className="mt-3 text-xs text-fg-muted">
						{s ? `${s.emitted.toLocaleString()} samples emitted, ${s.dropped.toLocaleString()} dropped by faults since start.` : ' '}
					</p>
				</Card>

				<Card title="Endpoints" className="lg:col-span-2">
					<ul className="divide-y divide-border">
						{meta.data?.protocols.map((p) => (
							<li key={p.protocol} className="flex items-center justify-between gap-3 py-2 text-sm">
								<span className="flex items-center gap-2">
									<ProtocolBadge protocol={p.protocol} muted={!p.enabled} />
									{p.note && <span className="text-xs text-fg-muted">{p.note}</span>}
								</span>
								<span className="font-mono text-xs text-fg-muted">
									{p.enabled ? (p.port ? `${p.host}:${p.port}` : p.host) : <Badge>off</Badge>}
								</span>
							</li>
						))}
					</ul>
				</Card>
			</div>

			{problems.length > 0 && (
				<Card title="Needs attention" className="mt-4">
					<ul className="divide-y divide-border">
						{problems.map((i) => (
							<li key={i.id} className="flex items-center justify-between gap-3 py-2 text-sm">
								<Link to={`/instances/${i.id}`} className="font-medium hover:underline">
									{i.name}
								</Link>
								<span className="flex items-center gap-3">
									<span className="max-w-md truncate text-xs text-fg-muted">{i.statusDetail}</span>
									<StatusDot status={i.status} />
								</span>
							</li>
						))}
					</ul>
				</Card>
			)}

			<Card title="Recently changed" className="mt-4" actions={<Link to="/instances" className="text-xs text-accent hover:underline">All sensors</Link>}>
				{instances.data?.length ? (
					<ul className="divide-y divide-border">
						{[...instances.data]
							.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
							.slice(0, 6)
							.map((i) => (
								<li key={i.id} className="flex items-center justify-between gap-3 py-2 text-sm">
									<Link to={`/instances/${i.id}`} className="min-w-0 truncate font-medium hover:underline">
										{i.name}
									</Link>
									<span className="flex shrink-0 items-center gap-3">
										<span className="hidden text-xs text-fg-muted sm:inline">{defs.data?.find((d) => d.id === i.definitionId)?.name}</span>
										<span className="text-xs text-fg-muted">{timeAgo(i.updatedAt)}</span>
										<StatusDot status={i.status} label={false} />
									</span>
								</li>
							))}
					</ul>
				) : (
					<p className="text-sm text-fg-muted">
						No sensors yet. <Link to="/definitions" className="text-accent hover:underline">Pick a definition</Link> to create one.
					</p>
				)}
			</Card>
		</>
	);
}
